import { CopyIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui/components/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/components/select';
import { Switch } from '@/shared/ui/components/switch';
import { cn } from '@/shared/lib/utils';
import type { Weekday } from './operatingHours';
import {
  closesAfterOpening,
  closeTimeOptions,
  MIDNIGHT_CLOSE,
  type EditableWeekdayHours,
} from './operatingHoursForm';
import { slotTimeOptions } from './slotTimeGrid';

function patchDay(
  hours: EditableWeekdayHours[],
  weekday: Weekday,
  patch: Partial<EditableWeekdayHours>,
): EditableWeekdayHours[] {
  return hours.map((row) => (row.weekday === weekday ? { ...row, ...patch } : row));
}

/**
 * The weekday rows of VenueOperatingHours, picked only from the Venue's SlotGranularity grid
 * (ADR 0081): opens from every grid time, closes from the later grid times then midnight
 * (ADR 0080). Shared by Create Venue and the workspace Operating hours page.
 */
export function OperatingHoursPicker({
  slotGranularityMinutes,
  hours,
  onHours,
  disabled = false,
}: {
  slotGranularityMinutes: number;
  hours: EditableWeekdayHours[];
  onHours: (next: EditableWeekdayHours[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation('common');
  const openTimes = slotTimeOptions(slotGranularityMinutes);

  function copyMondayToAll() {
    const monday = hours[0];
    if (!monday) return;
    onHours(
      hours.map((row) =>
        row.closed ? row : { ...row, openTime: monday.openTime, closeTime: monday.closeTime },
      ),
    );
  }

  return (
    <div className="rounded-lg border">
      <div className="flex min-h-11 items-center justify-between gap-2 border-b px-4 py-2">
        <p className="text-sm font-medium">{t('venues.operatingHours.picker.hoursTitle')}</p>
        {!disabled && (
          <Button type="button" variant="ghost" size="sm" onClick={copyMondayToAll}>
            <CopyIcon className="size-3.5" />
            {t('venues.operatingHours.picker.copyMonday')}
          </Button>
        )}
      </div>
      <div className="divide-y">
        {hours.map((row) => {
          const dayLabel = t(`venues.operatingHours.weekdays.${row.weekday.toLowerCase()}`);
          return (
            <div
              key={row.weekday}
              className="grid grid-cols-[auto_6rem_1fr] items-center gap-3 px-4 py-2.5"
            >
              <Switch
                aria-label={t('venues.operatingHours.picker.openToggle', { day: dayLabel })}
                checked={!row.closed}
                disabled={disabled}
                onCheckedChange={(checked) =>
                  onHours(
                    patchDay(hours, row.weekday, {
                      closed: !checked,
                      openTime: checked ? row.openTime : '',
                      closeTime: checked ? row.closeTime : '',
                    }),
                  )
                }
              />
              <span
                className={cn(
                  'truncate text-sm font-medium',
                  row.closed && 'text-muted-foreground line-through',
                )}
              >
                {dayLabel}
              </span>
              {row.closed ? (
                <span className="text-muted-foreground text-sm">
                  {t('venues.operatingHours.picker.closed')}
                </span>
              ) : (
                <div className="flex items-center gap-2">
                  <Select
                    value={row.openTime}
                    disabled={disabled}
                    onValueChange={(value) =>
                      onHours(
                        patchDay(hours, row.weekday, {
                          openTime: value,
                          closeTime: closesAfterOpening(value, row.closeTime) ? row.closeTime : '',
                        }),
                      )
                    }
                  >
                    <SelectTrigger
                      className="w-32"
                      aria-label={t('venues.operatingHours.fields.openTime', { day: dayLabel })}
                    >
                      <SelectValue
                        placeholder={t('venues.operatingHours.picker.timePlaceholder')}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {openTimes.map((time) => (
                        <SelectItem key={time} value={time}>
                          {time}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-muted-foreground text-sm">
                    {t('venues.operatingHours.picker.timeSeparator')}
                  </span>
                  <Select
                    value={row.closeTime}
                    disabled={disabled}
                    onValueChange={(value) =>
                      onHours(patchDay(hours, row.weekday, { closeTime: value }))
                    }
                  >
                    <SelectTrigger
                      className="w-32"
                      aria-label={t('venues.operatingHours.fields.closeTime', { day: dayLabel })}
                    >
                      <SelectValue
                        placeholder={t('venues.operatingHours.picker.timePlaceholder')}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {closeTimeOptions(row.openTime, slotGranularityMinutes).map((time) => (
                        <SelectItem key={time} value={time}>
                          {time === MIDNIGHT_CLOSE
                            ? t('venues.operatingHours.picker.midnightClose')
                            : time}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
